(() => {
  const MOBILE_QUERY = '(max-width: 1024px)';

  const initDesign = (root) => {
    if (root.dataset.gm3DesignReady === 'true') return;
    root.dataset.gm3DesignReady = 'true';

    const tabs = Array.from(root.querySelectorAll('[data-gm3-design-tab]'));
    const tabsNav = root.querySelector('[data-gm3-design-tabs-nav]');
    const tabsViewport = root.querySelector('[data-gm3-design-tabs-viewport]');
    const tabsTrack = root.querySelector('[data-gm3-design-tabs-track]');
    const tabsPrevBtn = root.querySelector('[data-gm3-design-tabs-prev]');
    const tabsNextBtn = root.querySelector('[data-gm3-design-tabs-next]');
    const cards = Array.from(root.querySelectorAll('[data-gm3-design-item]'));
    const viewport = root.querySelector('[data-gm3-design-viewport]');
    const track = root.querySelector('[data-gm3-design-track]');
    const slides = Array.from(root.querySelectorAll('[data-gm3-design-slide]'));
    const dotsWrap = root.querySelector('[data-gm3-design-dots]');
    const mediaQuery = window.matchMedia(MOBILE_QUERY);
    const autoplayEnabled = root.dataset.gm3DesignAutoplay === 'true';

    if (tabs.length === 0 || cards.length === 0) return;

    let activeTab = tabs[0].dataset.gm3DesignTab;
    let activeIndex = Number.parseInt(cards[0].dataset.gm3DesignIndex || '0', 10);
    let visibleSlides = [];
    let visibleSlideIndex = 0;
    let minX = 0;
    let currentX = 0;
    let resizeTimer = null;
    let tabsMinX = 0;
    let tabsCurrentX = 0;
    let tabsSuppressClick = false;
    let wheelSwitchLocked = false;
    let wheelSwitchTimer = null;
    const pausedIndexes = new Set();

    const drag = {
      pointerId: null,
      startX: 0,
      lastX: 0,
      startTranslate: 0,
      moved: false,
    };

    const tabsDrag = {
      pointerId: null,
      startX: 0,
      lastX: 0,
      startTranslate: 0,
      moved: false,
      tapped: false,
    };

    const getVideo = (element) => element?.querySelector('video');

    const getElementIndex = (element) => Number.parseInt(element?.dataset.gm3DesignIndex || '-1', 10);

    const getElementsByIndex = (index) =>
      [...cards, ...slides].filter((element) => getElementIndex(element) === index);

    const setVideoButtonState = (button, playing) => {
      if (!button) return;
      const playIcon = button.querySelector('.gm3-design__play-icon--play');
      const pauseIcon = button.querySelector('.gm3-design__play-icon--pause');
      if (playIcon) playIcon.hidden = playing;
      if (pauseIcon) pauseIcon.hidden = !playing;
      button.setAttribute('aria-pressed', playing ? 'true' : 'false');
      button.setAttribute('aria-label', playing ? button.dataset.labelPause || 'Pause video' : button.dataset.labelPlay || 'Play video');
    };

    const updateAllVideoButtons = () => {
      [...cards, ...slides].forEach((element) => {
        const video = getVideo(element);
        const button = element.querySelector('[data-gm3-design-video-toggle]');
        setVideoButtonState(button, Boolean(video && !video.paused));
      });
    };

    const syncVideos = () => {
      [...cards, ...slides].forEach((element) => {
        const video = getVideo(element);
        if (!video) {
          setVideoButtonState(element.querySelector('[data-gm3-design-video-toggle]'), false);
          return;
        }

        const isMobileSlide = element.hasAttribute('data-gm3-design-slide');
        const elementIndex = getElementIndex(element);
        const shouldPlay =
          autoplayEnabled &&
          !pausedIndexes.has(elementIndex) &&
          !element.hidden &&
          element.classList.contains('is-active') &&
          (isMobileSlide ? mediaQuery.matches : !mediaQuery.matches);

        if (shouldPlay) {
          video.muted = true;
          video.playsInline = true;
          video.play().then(updateAllVideoButtons).catch(updateAllVideoButtons);
        } else {
          video.pause();
          updateAllVideoButtons();
        }
      });
    };

    const getFirstIndexForTab = (tabName) => {
      const first = cards.find((card) => card.dataset.gm3DesignTabName === tabName);
      return Number.parseInt(first?.dataset.gm3DesignIndex || '0', 10);
    };

    const getActiveTabIndex = () => Math.max(0, tabs.findIndex((tab) => tab.dataset.gm3DesignTab === activeTab));

    const getTabNameByIndex = (index) => {
      if (tabs.length === 0) return activeTab;
      const clampedIndex = Math.max(0, Math.min(tabs.length - 1, index));
      return tabs[clampedIndex].dataset.gm3DesignTab;
    };

    const setActiveCards = () => {
      cards.forEach((card) => {
        const itemIndex = getElementIndex(card);
        const active = itemIndex === activeIndex && card.dataset.gm3DesignTabName === activeTab;
        const inTab = card.dataset.gm3DesignTabName === activeTab;
        card.hidden = !inTab;
        card.classList.toggle('is-active', active);
      });
    };

    const buildDots = () => {
      if (!dotsWrap) return;
      dotsWrap.innerHTML = '';

      const activeTabIndex = getActiveTabIndex();

      tabs.forEach((_, index) => {
        const dot = document.createElement('button');
        dot.className = `common-carousel-dot${index === activeTabIndex ? ' is-active' : ''}`;
        dot.type = 'button';
        dot.setAttribute('aria-label', `Show design tab ${index + 1}`);
        dot.addEventListener('click', () => setTab(getTabNameByIndex(index)));
        dotsWrap.appendChild(dot);
      });
    };

    const updateDots = () => {
      if (!dotsWrap) return;
      const activeTabIndex = getActiveTabIndex();
      Array.from(dotsWrap.children).forEach((dot, index) => {
        dot.classList.toggle('is-active', index === activeTabIndex);
      });
    };

    const clampX = (x) => Math.max(minX, Math.min(0, x));

    const clampTabsX = (x) => Math.max(tabsMinX, Math.min(0, x));

    const normalizeWheelDelta = (event) => {
      if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) return event.deltaX * 16;
      if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) return event.deltaX * viewport.clientWidth;
      return event.deltaX;
    };

    const normalizeTabsWheelDelta = (event) => {
      if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) return event.deltaX * 16;
      if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) return event.deltaX * tabsViewport.clientWidth;
      return event.deltaX;
    };

    const updateTabsOverflowState = () => {
      if (!tabsNav) return;
      const hasOverflow = tabsMinX < 0;
      tabsNav.classList.toggle('is-overflowing', hasOverflow);
      tabsNav.classList.toggle('has-overflow-left', hasOverflow && tabsCurrentX < -1);
      tabsNav.classList.toggle('has-overflow-right', hasOverflow && tabsCurrentX > tabsMinX + 1);
    };

    const setTabsX = (x, animate = false) => {
      if (!tabsTrack) return;
      tabsCurrentX = clampTabsX(x);
      tabsTrack.style.transition = animate ? 'transform 320ms ease' : 'none';
      tabsTrack.style.transform = `translate3d(${tabsCurrentX}px, 0, 0)`;
      updateTabsOverflowState();
    };

    const refreshTabsBounds = () => {
      if (!tabsViewport || !tabsTrack) return;
      const overflow = tabsTrack.scrollWidth - tabsViewport.clientWidth;
      tabsMinX = overflow > 1 ? -overflow : 0;
      setTabsX(tabsMinX === 0 ? 0 : tabsCurrentX);
    };

    const scheduleTabsRefresh = () => {
      refreshTabsBounds();
      requestAnimationFrame(refreshTabsBounds);
      window.setTimeout(refreshTabsBounds, 80);
    };

    const ensureTabVisible = (tab, animate = true) => {
      if (!tab || !tabsViewport || !tabsTrack || tabsMinX === 0) return;

      const viewportRect = tabsViewport.getBoundingClientRect();
      const tabRect = tab.getBoundingClientRect();
      const styles = window.getComputedStyle(tabsNav || tabsViewport);
      const edgeSize = parseFloat(styles.getPropertyValue('--gm3-design-tabs-edge-size')) || 60;
      const safeInset = edgeSize + 4;
      let nextX = tabsCurrentX;

      if (tabRect.left < viewportRect.left + safeInset) {
        nextX += viewportRect.left + safeInset - tabRect.left;
      } else if (tabRect.right > viewportRect.right - safeInset) {
        nextX -= tabRect.right - (viewportRect.right - safeInset);
      }

      if (Math.abs(nextX - tabsCurrentX) > 0.5) {
        setTabsX(nextX, animate);
      }
    };

    const setX = (x, animate = false) => {
      if (!track) return;
      currentX = clampX(x);
      track.style.transition = animate ? 'transform 320ms ease' : 'none';
      track.style.transform = `translate3d(${currentX}px, 0, 0)`;
    };

    const getSlideTargetX = (slide) => {
      if (!viewport || !track || !slide) return 0;
      return currentX + viewport.getBoundingClientRect().left - slide.getBoundingClientRect().left;
    };

    const refreshBounds = () => {
      if (!viewport || !track || !mediaQuery.matches) {
        setX(0);
        return;
      }

      const overflow = track.scrollWidth - viewport.clientWidth;
      minX = overflow > 1 ? -overflow : 0;
      setX(currentX);
    };

    const setVisibleSlide = (index, animate = true) => {
      if (visibleSlides.length === 0) return;
      visibleSlideIndex = Math.max(0, Math.min(visibleSlides.length - 1, index));

      slides.forEach((slide) => slide.classList.remove('is-active'));
      const slide = visibleSlides[visibleSlideIndex];
      slide.classList.add('is-active');
      activeIndex = getElementIndex(slide);

      updateDots();
      refreshBounds();
      setX(getSlideTargetX(slide), animate);
      setActiveCards();
      syncVideos();
    };

    const refreshSlides = () => {
      visibleSlides = slides.filter((slide) => slide.dataset.gm3DesignTabName === activeTab);

      slides.forEach((slide) => {
        const inTab = slide.dataset.gm3DesignTabName === activeTab;
        slide.hidden = !inTab;
        slide.classList.remove('is-active');
      });

      visibleSlideIndex = Math.max(0, visibleSlides.findIndex((slide) =>
        getElementIndex(slide) === activeIndex
      ));
      if (visibleSlideIndex < 0) visibleSlideIndex = 0;
      buildDots();
      setVisibleSlide(visibleSlideIndex, false);
    };

    const setTab = (tabName) => {
      activeTab = tabName;
      activeIndex = getFirstIndexForTab(activeTab);
      let activeTabElement = null;

      tabs.forEach((tab) => {
        const active = tab.dataset.gm3DesignTab === activeTab;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        if (active) activeTabElement = tab;
      });

      setActiveCards();
      refreshSlides();
      ensureTabVisible(activeTabElement, true);
      syncVideos();
    };

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => setTab(tab.dataset.gm3DesignTab));
    });

    tabsPrevBtn?.addEventListener('click', () => {
      if (!tabsViewport) return;
      setTabsX(tabsCurrentX + tabsViewport.clientWidth * 0.65, true);
    });

    tabsNextBtn?.addEventListener('click', () => {
      if (!tabsViewport) return;
      setTabsX(tabsCurrentX - tabsViewport.clientWidth * 0.65, true);
    });

    tabsViewport?.addEventListener(
      'wheel',
      (event) => {
        if (tabsMinX === 0) return;

        const deltaX = normalizeTabsWheelDelta(event);
        if (Math.abs(deltaX) <= Math.abs(event.deltaY)) return;

        event.preventDefault();
        setTabsX(tabsCurrentX - deltaX);
      },
      { passive: false }
    );

    tabsViewport?.addEventListener('pointerdown', (event) => {
      if (tabsMinX === 0 || (event.pointerType === 'mouse' && event.button !== 0)) return;
      tabsSuppressClick = false;
      tabsDrag.pointerId = event.pointerId;
      tabsDrag.startX = event.clientX;
      tabsDrag.lastX = event.clientX;
      tabsDrag.startTranslate = tabsCurrentX;
      tabsDrag.moved = false;
      tabsDrag.tapped = false;
      tabsTrack.style.transition = 'none';
      tabsViewport.setPointerCapture?.(event.pointerId);
    });

    tabsViewport?.addEventListener('pointermove', (event) => {
      if (tabsDrag.pointerId !== event.pointerId) return;
      const deltaX = event.clientX - tabsDrag.startX;
      tabsDrag.lastX = event.clientX;
      if (!tabsDrag.moved && Math.abs(deltaX) > 8) tabsDrag.moved = true;
      if (!tabsDrag.moved) return;
      event.preventDefault();
      setTabsX(tabsDrag.startTranslate + deltaX);
    });

    const endTabsDrag = (event) => {
      if (tabsDrag.pointerId !== event.pointerId) return;
      tabsViewport?.releasePointerCapture?.(event.pointerId);

      const deltaX = tabsDrag.lastX - tabsDrag.startX;
      let handledTap = false;
      if (tabsDrag.moved && Math.abs(deltaX) > 12) {
        tabsSuppressClick = true;
        window.setTimeout(() => {
          tabsSuppressClick = false;
        }, 180);
      } else if (!tabsDrag.moved) {
        const target = document.elementFromPoint(event.clientX, event.clientY);
        const tab = target?.closest?.('[data-gm3-design-tab]');
        if (tab && tabsTrack?.contains(tab)) {
          tabsDrag.tapped = true;
          tabsSuppressClick = true;
          handledTap = true;
          setTab(tab.dataset.gm3DesignTab);
          window.setTimeout(() => {
            tabsSuppressClick = false;
          }, 0);
        }
      }

      tabsDrag.pointerId = null;
      tabsDrag.moved = false;
      if (!handledTap) setTabsX(tabsCurrentX);
    };

    tabsViewport?.addEventListener(
      'click',
      (event) => {
        if (!tabsSuppressClick) return;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );

    tabsViewport?.addEventListener('pointerup', endTabsDrag);
    tabsViewport?.addEventListener('pointercancel', endTabsDrag);
    tabsViewport?.addEventListener('lostpointercapture', () => {
      tabsDrag.pointerId = null;
      tabsDrag.moved = false;
      window.setTimeout(() => {
        tabsSuppressClick = false;
      }, 0);
    });
    tabsTrack?.addEventListener('dragstart', (event) => event.preventDefault());

    root.addEventListener(
      'pointerdown',
      (event) => {
        if (!event.target.closest('[data-gm3-design-video-toggle]')) return;
        event.stopPropagation();
      },
      true
    );

    root.addEventListener('click', (event) => {
      const toggle = event.target.closest('[data-gm3-design-video-toggle]');
      if (!toggle || !root.contains(toggle)) return;
      event.preventDefault();
      event.stopPropagation();

      const container = toggle.closest('[data-gm3-design-item], [data-gm3-design-slide]');
      const video = getVideo(container);
      if (!video) return;

      const itemIndex = getElementIndex(container);
      const matchingVideos = getElementsByIndex(itemIndex).map(getVideo).filter(Boolean);

      if (video.paused) {
        pausedIndexes.delete(itemIndex);
        video.muted = true;
        video.playsInline = true;
        video.play().then(updateAllVideoButtons).catch(updateAllVideoButtons);
      } else {
        pausedIndexes.add(itemIndex);
        matchingVideos.forEach((matchingVideo) => matchingVideo.pause());
        updateAllVideoButtons();
      }
    });

    [...cards, ...slides].forEach((element) => {
      const video = getVideo(element);
      if (!video) return;
      video.addEventListener('play', updateAllVideoButtons);
      video.addEventListener('pause', updateAllVideoButtons);
      video.addEventListener('ended', updateAllVideoButtons);
    });

    viewport?.addEventListener('pointerdown', (event) => {
      if (!mediaQuery.matches || tabs.length < 2) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      drag.pointerId = event.pointerId;
      drag.startX = event.clientX;
      drag.lastX = event.clientX;
      drag.startTranslate = currentX;
      drag.moved = false;
      viewport.setPointerCapture?.(event.pointerId);
      if (track) track.style.transition = 'none';
    });

    viewport?.addEventListener('pointermove', (event) => {
      if (drag.pointerId !== event.pointerId) return;
      const deltaX = event.clientX - drag.startX;
      drag.lastX = event.clientX;
      if (!drag.moved && Math.abs(deltaX) > 6) drag.moved = true;
      if (!drag.moved) return;
      setX(drag.startTranslate + deltaX);
      event.preventDefault();
    });

    const endDrag = (event) => {
      if (drag.pointerId !== event.pointerId) return;
      viewport?.releasePointerCapture?.(event.pointerId);
      drag.pointerId = null;

      if (!drag.moved) return;

      const delta = drag.lastX - drag.startX;
      const direction = delta < -20 ? 1 : delta > 20 ? -1 : 0;
      if (direction) {
        setTab(getTabNameByIndex(getActiveTabIndex() + direction));
      } else {
        setVisibleSlide(visibleSlideIndex, true);
      }
      drag.moved = false;
    };

    viewport?.addEventListener('pointerup', endDrag);
    viewport?.addEventListener('pointercancel', endDrag);
    viewport?.addEventListener('lostpointercapture', () => {
      drag.pointerId = null;
      drag.moved = false;
      setVisibleSlide(visibleSlideIndex, true);
    });
    track?.addEventListener('dragstart', (event) => event.preventDefault());

    viewport?.addEventListener(
      'wheel',
      (event) => {
        if (!mediaQuery.matches || tabs.length < 2) return;

        const deltaX = normalizeWheelDelta(event);
        if (Math.abs(deltaX) <= Math.abs(event.deltaY)) return;

        event.preventDefault();
        if (wheelSwitchLocked) return;

        const direction = deltaX > 20 ? 1 : deltaX < -20 ? -1 : 0;
        if (!direction) return;

        wheelSwitchLocked = true;
        window.clearTimeout(wheelSwitchTimer);
        wheelSwitchTimer = window.setTimeout(() => {
          wheelSwitchLocked = false;
        }, 1000);
        setTab(getTabNameByIndex(getActiveTabIndex() + direction));
      },
      { passive: false }
    );

    window.addEventListener('resize', () => {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(() => {
        scheduleTabsRefresh();
        ensureTabVisible(tabs.find((tab) => tab.dataset.gm3DesignTab === activeTab), false);
        refreshBounds();
        setVisibleSlide(visibleSlideIndex, false);
      }, 120);
    });

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', () => {
        refreshSlides();
        syncVideos();
      });
    }

    scheduleTabsRefresh();
    setTab(activeTab);
    updateAllVideoButtons();
    window.addEventListener('load', scheduleTabsRefresh, { once: true });
    document.fonts?.ready?.then(() => {
      scheduleTabsRefresh();
      ensureTabVisible(tabs.find((tab) => tab.dataset.gm3DesignTab === activeTab), false);
    }).catch(() => {});
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll('[data-gm3-design]').forEach(initDesign);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
